import * as THREE from 'three';
import { createNoise2D } from 'simplex-noise';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader.js';
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry.js';
import { Font, FontLoader } from 'three/examples/jsm/loaders/FontLoader.js';

// Structure pour stocker les informations sur la grille de la ville
export interface CityGrid {
  blocks: number[][];  // 0 = rue, 1 = marché noir, 2 = immeuble
  buildings: THREE.Mesh[];  // référence aux meshes des immeubles
  gridSize?: number;
  citySize?: number;
  blockSize?: number;
  roadWidth?: number;
}

// Mini Motorways style color palette
const COLORS = {
  GROUND: 0x91d9e8,  // Light blue for water/background
  ROAD: 0x555555,     // Lighter grey black roads
  WATER: 0x0099cc,    // Deeper blue for river
  BUILDING_COLORS: [
    0xff5a5a,  // Red
    0x3498ff,  // Blue
    0xffe234,  // Yellow
    0xff9e2d,  // Orange
    0x6ebf4b,  // Green
  ],
  BLACK_MARKET: 0x515151,  // Dark gray for black market
  HOME_MARKER: 0xff5500,   // Orange color for home marker
};

// Chemin vers la texture commune
const COMMON_TEXTURE_PATH = 'cartooncity_141_fbx/Texture_01.png';

export class CityGenerator {
  private scene: THREE.Scene;
  private noise2D: (x: number, y: number) => number;
  private citySize: number = 100;
  private blockSize: number = 5;
  private roadWidth: number = 2;
  private offset: number = this.citySize * (this.blockSize - this.roadWidth) / 4;
  private fbxLoader: FBXLoader;
  private assetCache: Map<string, THREE.Group> = new Map();
  private textureCache: Map<string, THREE.Texture> = new Map();
  private commonTexture: THREE.Texture | null = null;
  // Grille de la ville (maintenant fournie par le serveur)
  private grid: number[][] = [];  // 0 = rue, 1 = marché noir, 2 = immeuble
  // Liste des immeubles (pour référence ultérieure)
  private buildings: THREE.Mesh[] = [];
  // Référence au marché noir
  private blackMarket: THREE.Mesh | null = null;
  // Map to store home markers
  private homeMarkers: Map<string, THREE.Group> = new Map();
  // Map to store road signs
  private roadSigns: Map<string, THREE.Group> = new Map();
  private font: Font | null = null;
  private exitPortalBox: THREE.Box3 | null = null;
  private exitPortalGroup: THREE.Group | null = null;
  
  constructor(scene: THREE.Scene) {
    this.scene = scene;
    this.noise2D = createNoise2D();
    this.fbxLoader = new FBXLoader();
    
    // Load the font
    const fontLoader = new FontLoader();
    fontLoader.load(
      'https://threejs.org/examples/fonts/helvetiker_regular.typeface.json',
      (font) => {
        this.font = font;
        console.log('Font loaded successfully');
      },
      undefined,
      (error) => {
        console.error('Error loading font:', error);
      }
    );
    
    // Préchargement de la texture commune
    this.preloadCommonTexture();
  }
  
  // Préchargement de la texture commune
  private preloadCommonTexture(): void {
    const textureLoader = new THREE.TextureLoader();
    
    textureLoader.load(
      COMMON_TEXTURE_PATH,
      (texture) => {
        texture.colorSpace = THREE.SRGBColorSpace;
        texture.flipY = true; // Revenir au réglage d'origine qui fonctionnait
        
        // Augmenter la vivacité de la texture
        const enhancedTexture = this.enhanceTexture(texture);
        this.commonTexture = enhancedTexture;
        this.textureCache.set(COMMON_TEXTURE_PATH, enhancedTexture);
        console.log(`Texture commune chargée avec succès: ${COMMON_TEXTURE_PATH}`);
      },
      undefined,
      (error) => {
        console.error(`Erreur lors du chargement de la texture commune ${COMMON_TEXTURE_PATH}:`, error);
      }
    );
  }

  // Améliorer la vivacité des textures
  private enhanceTexture(texture: THREE.Texture): THREE.Texture {
    // Augmenter la saturation et la luminosité de la texture
    texture.encoding = THREE.sRGBEncoding;
    
    // Autres options de rendu pour améliorer l'apparence
    texture.anisotropy = 16; // Améliore la netteté des textures en angle
    texture.needsUpdate = true;
    
    return texture;
  }

  // Fonction générique pour charger un asset FBX avec la texture commune
  private loadFBXAsset(
    assetPath: string, 
    position: THREE.Vector3, 
    rotation: THREE.Euler = new THREE.Euler(0, 0, 0),
    scale: THREE.Vector3 = new THREE.Vector3(1, 1, 1)
  ): void {
    console.log(`Tentative de chargement de l'asset: ${assetPath}`);
    
    // Utiliser un identifiant unique pour l'instance de l'objet
    const instanceId = `${assetPath}_${position.x}_${position.y}_${position.z}`;
    
    // Extraire le chemin de base pour configurer le loader
    const pathParts = assetPath.split('/');
    const basePath = pathParts.slice(0, pathParts.length - 1).join('/') + '/';
    this.fbxLoader.setPath(basePath);
    console.log(`Chemin de base configuré: ${basePath}`);
    
    // Le nom du fichier sans le chemin
    const fileName = pathParts[pathParts.length - 1];
    console.log(`Nom du fichier à charger: ${fileName}`);
    
    // Si la texture commune n'est pas encore chargée, la mettre en file d'attente
    if (this.commonTexture === null) {
      console.log(`La texture commune n'est pas encore chargée, mise en file d'attente du modèle ${assetPath}`);
      // Essayer à nouveau dans 100ms
      setTimeout(() => {
        this.loadFBXAsset(assetPath, position, rotation, scale);
      }, 100);
      return;
    }
    
    // Utiliser la texture commune déjà chargée
    this.processFBXLoading(fileName, basePath, this.commonTexture, position, rotation, scale);
  }
  
  // Version alternative qui utilise un matériau plutôt qu'une texture
  private processFBXLoadingWithMaterial(
    fileName: string,
    basePath: string,
    material: THREE.Material,
    position: THREE.Vector3,
    rotation: THREE.Euler,
    scale: THREE.Vector3
  ): void {
    const fullPath = basePath + fileName;
    
    console.log(`Tentative de chargement du modèle FBX: ${fullPath}`);
    
    // Vérifier si le modèle est déjà dans le cache
    if (this.assetCache.has(fullPath)) {
      console.log(`Modèle trouvé dans le cache: ${fullPath}`);
      // Utiliser le modèle en cache (cloner pour avoir une nouvelle instance)
      const cachedModel = this.assetCache.get(fullPath)!;
      const modelClone = cachedModel.clone();
      
      // Appliquer les transformations
      modelClone.scale.copy(scale);
      modelClone.position.copy(position);
      modelClone.rotation.copy(rotation);
      
      // Ajouter le clone à la scène
      this.scene.add(modelClone);
    } else {
      // Charger le modèle FBX s'il n'est pas en cache
      try {
        this.fbxLoader.load(
          fileName,
          (fbx) => {
            console.log(`Modèle FBX chargé avec succès: ${fileName}`);
            // Appliquer le matériau à tous les meshes du modèle
            fbx.traverse((child) => {
              if (child instanceof THREE.Mesh) {
                child.castShadow = true;
                child.receiveShadow = true;
                
                // Appliquer le matériau
                if (Array.isArray(child.material)) {
                  child.material = Array(child.material.length).fill(material.clone());
                } else {
                  child.material = material.clone();
                }
              }
            });
            
            // Ajouter le modèle original au cache (avant application des transformations)
            this.assetCache.set(fullPath, fbx);
            
            // Créer un clone pour cette instance spécifique
            const modelInstance = fbx.clone();
            
            // Appliquer les transformations à l'instance
            modelInstance.scale.copy(scale);
            modelInstance.position.copy(position);
            modelInstance.rotation.copy(rotation);
            
            // Ajouter l'instance à la scène
            this.scene.add(modelInstance);
          },
          // Callback de progression
          (xhr) => {
            console.log(`${fileName} ${(xhr.loaded / xhr.total) * 100}% loaded`);
          },
          // Callback d'erreur
          (error) => {
            console.error(`Erreur lors du chargement du modèle ${fileName}:`, error);
            
            // En cas d'erreur, créer un objet de remplacement simple
            this.createFallbackObject(position, rotation, scale, material);
          }
        );
      } catch (err) {
        console.error(`Exception lors du chargement du modèle ${fileName}:`, err);
        this.createFallbackObject(position, rotation, scale, material);
      }
    }
  }
  
  // Fonction pour créer un objet de remplacement simple avec un matériau au lieu d'une texture
  private createFallbackObject(
    position: THREE.Vector3,
    rotation: THREE.Euler,
    scale: THREE.Vector3,
    material: THREE.Material | THREE.Texture
  ): void {
    console.log("Création d'un objet de remplacement pour l'échec du chargement FBX");
    
    // Créer un objet simple comme remplacement
    const geometry = new THREE.BoxGeometry(2, 2, 2);
    let finalMaterial: THREE.Material;
    
    if (material instanceof THREE.Texture) {
      finalMaterial = new THREE.MeshPhongMaterial({ 
        map: material,
        color: 0x1a3c8c  // Bleu foncé pour la police
      });
    } else {
      finalMaterial = material;
    }
    
    const fallbackMesh = new THREE.Mesh(geometry, finalMaterial);
    fallbackMesh.position.copy(position);
    fallbackMesh.rotation.copy(rotation);
    // Ajuster l'échelle pour la visibilité (20x plus grand)
    fallbackMesh.scale.set(
      scale.x * 20, 
      scale.y * 20, 
      scale.z * 20
    );
    
    fallbackMesh.castShadow = true;
    fallbackMesh.receiveShadow = true;
    
    this.scene.add(fallbackMesh);
  }

  // Nouvelle méthode pour recevoir la grille du serveur
  public setGrid(serverGrid: {blocks: number[][], gridSize?: number, citySize?: number, blockSize?: number, roadWidth?: number}): void {
    this.grid = serverGrid.blocks;
    
    // Mettre à jour les paramètres si fournis par le serveur
    if (serverGrid.citySize) this.citySize = serverGrid.citySize;
    if (serverGrid.blockSize) this.blockSize = serverGrid.blockSize;
    if (serverGrid.roadWidth) this.roadWidth = serverGrid.roadWidth;
    
    console.log("Grid received from server", this.grid.length, "x", this.grid[0]?.length);
  }

  // méthode pour calculer la position d'une case à partir de son index dans la grille
  private calculatePositionFromIndex(index: number): number {
    // Équivalent TypeScript de: (i+1) // 2 * generator.road_width + (i // 2)* generator.block_size
    // // en Python est la division entière, en JS/TS on utilise Math.floor(a/b)
    let value = Math.floor((index + 1) / 2) * this.roadWidth + Math.floor(index / 2) * this.blockSize - this.offset;
    return value;
  }

  // méthode pour calculer la position d'une case à partir de ses coordonnées dans la grille
  public calculatePosition(i: number, j: number): THREE.Vector3 {
    let x = 0;
    let z = 0;
    
    // Pour les coordonnées x (i)
    x = this.calculatePositionFromIndex(i);
    z = this.calculatePositionFromIndex(j);
   
    
    // Ajuster par rapport au centre
    return new THREE.Vector3(x, 0, z);
  }

  // méthode pour calculer les coordonnées de la grille à partir d'une position 3D (inverse de calculatePosition)
  public calculateIndex(position: THREE.Vector3): { i: number, j: number } {
    let x = position.x;
    let z = position.z;
    
    // Ajouter l'offset aux coordonnées (puisque nous soustrayons l'offset dans calculatePosition)
    const adjustedX = x + this.offset;
    const adjustedZ = z + this.offset;
    
    // Calculer i et j en utilisant la formule directe (équivalent Python)
    const calcInverseCoord = (pos: number): number => {
      // Division par la somme de la largeur de la route et de la taille du bloc
      const division = pos / (this.roadWidth + this.blockSize);
      // Obtenir la partie entière de la division
      const quotient = Math.floor(division);
      // Calculer le reste
      const remainder = pos - quotient * (this.roadWidth + this.blockSize);
      
      // Calculer l'index
      let idx = quotient * 2;
      if (remainder >= this.roadWidth) {  // Changed from > to >=
        idx += 1;
      }
      
      return idx;
    };
    
    // Calculer les indices i et j
    const i = calcInverseCoord(adjustedX);
    const j = calcInverseCoord(adjustedZ);
    
    return { i, j };
  }

  // méthode pour calculer la taille d'une case à partir de ses coordonnées dans la grille
  private calculateSize(i: number, j: number): THREE.Vector3 {
    let width = i % 2 === 1 ? this.blockSize :  this.roadWidth;
    let height = j % 2 === 1 ? this.blockSize :  this.roadWidth;
    
    return new THREE.Vector3(width, height, 0);
  }

  private renderBackground():void{
    
    const lastGridIndex = this.grid.length - 1;
    const firstPos = this.calculatePosition(0, 0);
    const lastPos = this.calculatePosition(lastGridIndex, lastGridIndex).add(
      this.calculateSize(lastGridIndex, lastGridIndex)
    );
    
    
    // Use the larger dimension to ensure ground covers entire city
    let citySize = lastPos.x - firstPos.x;

    // Center the ground between first and last positions
    const groundX = (firstPos.x + lastPos.x) / 2;
    const groundZ = (firstPos.z + lastPos.z) / 2;
    const groundGeometry = new THREE.PlaneGeometry(citySize, citySize);
    const groundMaterial = new THREE.MeshPhongMaterial({ color: COLORS.GROUND });
    const ground = new THREE.Mesh(groundGeometry, groundMaterial);
    ground.rotation.x = -Math.PI / 2;
    ground.position.set(groundX, -0.2, groundZ);
    this.scene.add(ground);
    
    // Add GrassPlane_05.fbx at position (25, 25)
    this.loadNatureFBXAsset(
      'cartooncity_141_fbx/Road/GrassPlane_01.fbx',
      new THREE.Vector3(25, -0.3, 25),
      new THREE.Euler(0, 0, 0),
      new THREE.Vector3(500, 0.2, 500)
    );
  }

  private renderWater(i: number, j: number): void {
    const position = this.calculatePosition(i, j);
    const waterGeometry = new THREE.PlaneGeometry(this.blockSize, this.blockSize);
    const waterMaterial = new THREE.MeshPhongMaterial({ 
      color: COLORS.WATER,
      shininess: 90
    });
    const water = new THREE.Mesh(waterGeometry, waterMaterial);
    water.rotation.x = -Math.PI / 2;
    water.position.set(
      position.x + this.blockSize / 2,
      -0.1, // Altitude de -0.1
      position.z + this.blockSize / 2
    );
    this.scene.add(water);
  }

  private renderRoad(i: number, j: number): void {
    const position = this.calculatePosition(i, j);
    // Déterminer si c'est une route horizontale ou verticale
    const size = this.calculateSize(i, j);
    
    // Créer la géométrie adaptée à l'orientation de la route
    const roadGeometry = new THREE.PlaneGeometry(size.x, size.y);
    const roadMaterial = new THREE.MeshPhongMaterial({ 
      color: COLORS.ROAD,
      shininess: 10
    });
    
    const road = new THREE.Mesh(roadGeometry, roadMaterial);
    road.rotation.x = -Math.PI / 2;
    
    // Positionner la route au centre de sa case
    road.position.set(
      position.x + size.x / 2,
      0.01, // Légèrement au-dessus du sol pour éviter le z-fighting
      position.z + size.y / 2
    );
    
    this.scene.add(road);
  }

  // Fonctions de rendu pour les différents patterns de bâtiments

  // Pattern 10: Bâtiment 1x1
  private render10_1x1(i: number, j: number): void {
    const position = this.calculatePosition(i, j);
    const size = this.blockSize;
    
    // Créer un bâtiment simple 1x1
    this.renderBuilding(position.x, position.z, size, size, 1.5);
  }

  // Pattern 11: Bâtiment 1x2 (horizontal)
  private render11_1x2(i: number, j: number): void {
    const position = this.calculatePosition(i, j);
    const width = this.blockSize;
    const height = this.blockSize * 2;
    
    // Créer un bâtiment 1x2
    this.renderBuilding(position.x, position.z, width, height, 2);
  }

  // Pattern 12: Bâtiment 2x1 (vertical)
  private render12_2x1(i: number, j: number): void {
    const position = this.calculatePosition(i, j);
    const width = this.blockSize * 2;
    const height = this.blockSize;
    
    // Créer un bâtiment 2x1
    this.renderBuilding(position.x, position.z, width, height, 2);
  }

  // Pattern 13: Forme en L (orientation 1)
  private render13_L1(i: number, j: number): void {
    const position = this.calculatePosition(i, j);
    
    // Première partie du L
    this.renderBuilding(position.x, position.z, this.blockSize * 2, this.blockSize, 2);
    // Deuxième partie du L
    this.renderBuilding(position.x, position.z, this.blockSize, this.blockSize * 2, 2);
  }

  // Pattern 14: Forme en L (orientation 2)
  private render14_L2(i: number, j: number): void {
    const position = this.calculatePosition(i, j);
    
    // Première partie du L
    this.renderBuilding(position.x, position.z, this.blockSize * 2, this.blockSize, 2);
    // Deuxième partie du L (en bas à droite)
    this.renderBuilding(position.x + this.blockSize, position.z, this.blockSize, this.blockSize * 2, 2);
  }

  // Pattern 15: Forme en L (orientation 3)
  private render15_L3(i: number, j: number): void {
    const position = this.calculatePosition(i, j);
    
    // Première partie du L (horizontal en bas)
    this.renderBuilding(position.x, position.z + this.blockSize, this.blockSize * 2, this.blockSize, 2);
    // Deuxième partie du L (vertical à gauche)
    this.renderBuilding(position.x, position.z, this.blockSize, this.blockSize * 2, 2);
  }

  // Pattern 16: Forme en L (orientation 4)
  private render16_L4(i: number, j: number): void {
    const position = this.calculatePosition(i, j);
    
    // Première partie du L (horizontal en bas)
    this.renderBuilding(position.x, position.z + this.blockSize, this.blockSize * 2, this.blockSize, 2);
    // Deuxième partie du L (vertical à droite)
    this.renderBuilding(position.x + this.blockSize, position.z, this.blockSize, this.blockSize * 2, 2);
  }

  // Pattern 17: Bâtiment 3x1
  private render17_3x1(i: number, j: number): void {
    const position = this.calculatePosition(i, j);
    const width = this.blockSize * 3;
    const height = this.blockSize;
    
    // Créer un bâtiment 3x1
    this.renderBuilding(position.x, position.z, width, height, 2.2);
  }

  // Pattern 18: Bâtiment 1x3
  private render18_1x3(i: number, j: number): void {
    const position = this.calculatePosition(i, j);
    const width = this.blockSize;
    const height = this.blockSize * 3;
    
    // Créer un bâtiment 1x3
    this.renderBuilding(position.x, position.z, width, height, 2.2);
  }

  // Pattern 19: Bâtiment 2x2
  private render19_2x2(i: number, j: number): void {
    const position = this.calculatePosition(i, j);
    const size = this.blockSize * 2;
    
    // Créer un bâtiment carré 2x2
    this.renderBuilding(position.x, position.z, size, size, 2.5);
  }

  // Pattern 20: Bâtiment 4x1
  private render20_4x1(i: number, j: number): void {
    const position = this.calculatePosition(i, j);
    const width = this.blockSize * 4;
    const height = this.blockSize;
    
    // Créer un bâtiment 4x1
    this.renderBuilding(position.x, position.z, width, height, 2.3);
  }

  // Pattern 21: Bâtiment 1x4
  private render21_1x4(i: number, j: number): void {
    const position = this.calculatePosition(i, j);
    const width = this.blockSize;
    const height = this.blockSize * 4;
    
    // Créer un bâtiment 1x4
    this.renderBuilding(position.x, position.z, width, height, 2.3);
  }

  // Pattern 22: Bâtiment 3x2
  private render22_3x2(i: number, j: number): void {
    const position = this.calculatePosition(i, j);
    const width = this.blockSize * 3;
    const height = this.blockSize * 2;
    
    // Créer un bâtiment 3x2
    this.renderBuilding(position.x, position.z, width, height, 2.7);
  }

  // Pattern 23: Bâtiment 2x3
  private render23_2x3(i: number, j: number): void {
    const position = this.calculatePosition(i, j);
    const width = this.blockSize * 2;
    const height = this.blockSize * 3;
    
    // Créer un bâtiment 2x3
    this.renderBuilding(position.x, position.z, width, height, 2.7);
  }


  // Fonction d'aide pour rendre un bâtiment avec les dimensions spécifiées
  private renderBuilding(x: number, z: number, width: number, depth: number, height: number): void {
    // Choisir une couleur aléatoire parmi la palette
    const colorIndex = Math.floor(Math.random() * COLORS.BUILDING_COLORS.length);
    const color = new THREE.Color(COLORS.BUILDING_COLORS[colorIndex]);
    
    // Créer la géométrie du bâtiment
    const buildingGeometry = new THREE.BoxGeometry(width, height, depth);
    const buildingMaterial = new THREE.MeshPhongMaterial({ 
      color,
      flatShading: true,
      shininess: 0
    });
    
    const building = new THREE.Mesh(buildingGeometry, buildingMaterial);
    
    // Positionner le bâtiment
    building.position.set(
      x + width / 2,
      height / 2,  // Moitié de la hauteur pour le placer sur le sol
      z + depth / 2
    );
    
    this.scene.add(building);
    this.buildings.push(building);
  }

  // Fonction d'aide pour ajouter des arbres à un espace vert
  private addTrees(x: number, z: number): void {
    // Nombre d'arbres à placer (environ 1 arbre pour 10 unités carrées)
    if (Math.random() > 0.1) {
      return;
    }
      
    // Sélectionner aléatoirement un type d'arbre parmi les 3 disponibles
    const treeTypes = [
      'cartooncity_141_fbx/Nature/Tree_01.fbx',
      'cartooncity_141_fbx/Nature/Tree_02.fbx',
      'cartooncity_141_fbx/Nature/Tree_03.fbx'
    ];
    
    const randomTreeIndex = Math.floor(Math.random() * treeTypes.length);
    const selectedTree = treeTypes[randomTreeIndex];
      
    // Chargement du modèle FBX d'arbre avec traitement spécial pour matériaux naturels
    this.loadNatureFBXAsset(
      selectedTree,
      new THREE.Vector3(x+0.2, 0, z+0.2),
      new THREE.Euler(0, Math.random() * Math.PI * 2, 0), // Rotation aléatoire
      new THREE.Vector3(0.01, 0.01, 0.01) // Échelle adaptée
    );
  }
  
  // Rendu d'un espace vert générique
  private renderGreenSpace(i: number, j: number): void {
    const position = this.calculatePosition(i, j);
    const size = this.calculateSize(i, j);

    const sizeX = size.x;
    const sizeZ = size.y;
    const factor = 0.34;

    console.log("green space", i, j, sizeX, sizeZ, "position", position);

    
    // Charger le modèle 3D d'herbe à partir des assets avec traitement spécial pour matériaux naturels
    this.loadNatureFBXAsset(
      'cartooncity_141_fbx/Road/GrassPlane_01.fbx',
      new THREE.Vector3(
        position.x + sizeX / 2,
        0.01, // Position au niveau du sol
        position.z + sizeZ / 2
      ),
      new THREE.Euler(0, 0, 0), // Rotation pour que le plan soit horizontal
      new THREE.Vector3(sizeX * factor, 0.2 , sizeZ * factor)  // Échelle adaptée à la taille du bloc
    );
    
    // Ajouter des arbres à l'espace vert
    this.addTrees(position.x, position.z);
  }
  
  // Fonction spécialisée pour charger des assets naturels avec moins de brillance
  private loadNatureFBXAsset(
    assetPath: string, 
    position: THREE.Vector3, 
    rotation: THREE.Euler = new THREE.Euler(0, 0, 0),
    scale: THREE.Vector3 = new THREE.Vector3(1, 1, 1)
  ): void {
    console.log(`Chargement d'asset naturel: ${assetPath}`);
    
    // Extraire le chemin de base pour configurer le loader
    const pathParts = assetPath.split('/');
    const basePath = pathParts.slice(0, pathParts.length - 1).join('/') + '/';
    this.fbxLoader.setPath(basePath);
    
    // Le nom du fichier sans le chemin
    const fileName = pathParts[pathParts.length - 1];
    
    // Si la texture commune n'est pas encore chargée, la mettre en file d'attente
    if (this.commonTexture === null) {
      console.log(`La texture commune n'est pas encore chargée, mise en file d'attente du modèle ${assetPath}`);
      // Essayer à nouveau dans 100ms
      setTimeout(() => {
        this.loadNatureFBXAsset(assetPath, position, rotation, scale);
      }, 100);
      return;
    }
    
    const fullPath = basePath + fileName;
    
    // Couleur verte par défaut pour les éléments naturels
    const natureGreen = new THREE.Color(0x2e8b57); // Sea Green
    const treeGreen = new THREE.Color(0x228b22);   // Forest Green
    const grassGreen = new THREE.Color(0x7cfc00);  // Lawn Green
    
    // Vérifier si le modèle est déjà dans le cache
    if (this.assetCache.has(fullPath)) {
      console.log(`Modèle naturel trouvé dans le cache: ${fullPath}`);
      // Utiliser le modèle en cache (cloner pour avoir une nouvelle instance)
      const cachedModel = this.assetCache.get(fullPath)!;
      const modelClone = cachedModel.clone();
      
      // Appliquer les transformations
      modelClone.scale.copy(scale);
      modelClone.position.copy(position);
      modelClone.rotation.copy(rotation);
      
      // Ajouter le clone à la scène
      this.scene.add(modelClone);
    } else {
      // Charger le modèle FBX s'il n'est pas en cache
      try {
        this.fbxLoader.load(
          fileName,
          (fbx) => {
            console.log(`Modèle naturel FBX chargé avec succès: ${fileName}`);
            
            // Déterminer si c'est un arbre ou de l'herbe pour choisir la couleur appropriée
            const isTree = fileName.toLowerCase().includes('tree');
            const baseColor = isTree ? treeGreen : grassGreen;
            
            // Appliquer des matériaux verts à tous les meshes du modèle
            fbx.traverse((child) => {
              if (child instanceof THREE.Mesh) {
                child.castShadow = true;
                child.receiveShadow = true;
                
                // Ajouter une légère variation à la couleur verte
                const colorVariation = 0.1; // 10% de variation
                const randomColor = new THREE.Color(
                  baseColor.r * (1 + (Math.random() - 0.5) * colorVariation),
                  baseColor.g * (1 + (Math.random() - 0.5) * colorVariation),
                  baseColor.b * (1 + (Math.random() - 0.5) * colorVariation)
                );
                
                if (Array.isArray(child.material)) {
                  child.material.forEach((mat, index) => {
                    // Créer un nouveau matériau pour chaque slot
                    if (mat instanceof THREE.MeshPhongMaterial) {
                      const newMat = new THREE.MeshPhongMaterial({
                        map: this.commonTexture,
                        color: randomColor,
                        shininess: 5,
                        flatShading: true,
                        name: 'nature_material'
                      });
                      child.material[index] = newMat;
                    } else {
                      // Pour les autres types de matériaux, créer un MeshPhongMaterial
                      const newMat = new THREE.MeshPhongMaterial({
                        map: this.commonTexture,
                        color: randomColor,
                        shininess: 5,
                        flatShading: true,
                        name: 'nature_material'
                      });
                      child.material[index] = newMat;
                    }
                  });
                } else {
                  // Remplacer le matériau unique par un nouveau qui préserve la texture mais ajuste la couleur
                  const newMat = new THREE.MeshPhongMaterial({
                    map: this.commonTexture, 
                    color: randomColor,
                    shininess: 5,
                    flatShading: true,
                    name: 'nature_material'
                  });
                  child.material = newMat;
                }
              }
            });
            
            // Ajouter le modèle original au cache (avant application des transformations)
            this.assetCache.set(fullPath, fbx);
            
            // Créer un clone pour cette instance spécifique
            const modelInstance = fbx.clone();
            
            // Appliquer les transformations à l'instance
            modelInstance.scale.copy(scale);
            modelInstance.position.copy(position);
            modelInstance.rotation.copy(rotation);
            
            // Ajouter l'instance à la scène
            this.scene.add(modelInstance);
          },
          // Callback de progression
          (xhr) => {
            console.log(`${fileName} ${(xhr.loaded / xhr.total) * 100}% loaded`);
          },
          // Callback d'erreur
          (error) => {
            console.error(`Erreur lors du chargement du modèle naturel ${fileName}:`, error);
            // En cas d'erreur, utiliser la méthode standard comme fallback
            this.loadFBXAsset(assetPath, position, rotation, scale);
          }
        );
      } catch (err) {
        console.error(`Exception lors du chargement du modèle naturel ${fileName}:`, err);
        // En cas d'erreur, utiliser la méthode standard comme fallback
        this.loadFBXAsset(assetPath, position, rotation, scale);
      }
    }
  }

  public generate(): void {
    // Create ground with Mini Motorways style light blue
    this.renderBackground();

    // Calculate ground size based on grid dimensions
    
    
    // Vérifier que la grille existe
    if (!this.grid || this.grid.length === 0) {
      console.error("No grid data available! Make sure to call setGrid() with server data before generate()");
      return;
    }
    
    // Parcourir toute la grille
    const gridSize = this.grid.length;
    
    for (let i = 0; i < gridSize; i++) {
      for (let j = 0; j < gridSize; j++) {
        const value = this.grid[i][j];
        if (value === 1) { // Si la valeur est 1 (eau)
          // Créer un carré bleu à une altitude de -0.1
          this.renderWater(i, j);
        } else if (value === 5 || value === 6 || value === 7 || value === 12 || value === 21) { // Si la valeur est route
          // Si c'est une position de route (coordonnée paire)
          this.renderRoad(i, j);
          
        } else if (value === 10) {
          this.renderBlackMarket(i, j);
          // this.renderPortal(i, j);
        } else if (value === 20) {
          this.renderPoliceStation(i, j);
        } else if (value === 30) {
          this.renderGazStation(i, j);
        } else if (value === 40) {
          this.renderPortal(i, j);
        } else if (value ===2) {
          this.renderGreenSpace(i, j); // Par défaut 1x1 pour couvrir juste la case
        }
      }
    }
    
    console.log("Grid rendered successfully - showing grid, blue squares for water, and roads");
  }
  
  // Méthode pour obtenir la grille de la ville (utile pour les déplacements)
  public getCityGrid(): CityGrid {
    return {
      blocks: this.grid,
      buildings: this.buildings
    };
  }

  // Getter for offset property
  public getOffset(): number {
    return this.offset;
  }

  // Getter for roadWidth property
  public getRoadWidth(): number {
    return this.roadWidth;
  }

  // Getter for blockSize property
  public getBlockSize(): number {
    return this.blockSize;
  }

  // Méthode pour nettoyer les ressources en cache
  public cleanup(): void {
    // Nettoyer les textures
    this.textureCache.forEach((texture) => {
      texture.dispose();
    });
    this.textureCache.clear();
    
    // Nettoyer les modèles 3D
    this.assetCache.forEach((model) => {
      model.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          if (child.geometry) {
            child.geometry.dispose();
          }
          
          if (child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach((mat) => {
                if (mat.map) mat.map.dispose();
                mat.dispose();
              });
            } else {
              if (child.material.map) child.material.map.dispose();
              child.material.dispose();
            }
          }
        }
      });
    });
    this.assetCache.clear();
    
    // Also clean up portal labels
    this.scene.traverse((object) => {
      if (object instanceof THREE.Mesh && object.userData.isPortalLabel) {
        this.scene.remove(object);
      }
    });
  }

  private processFBXLoading(
    fileName: string,
    basePath: string,
    texture: THREE.Texture,
    position: THREE.Vector3,
    rotation: THREE.Euler,
    scale: THREE.Vector3
  ): void {
    const fullPath = basePath + fileName;
    
    console.log(`Tentative de chargement du modèle FBX avec texture: ${fullPath}`);
    
    // Vérifier si le modèle est déjà dans le cache
    if (this.assetCache.has(fullPath)) {
      console.log(`Modèle trouvé dans le cache: ${fullPath}`);
      // Utiliser le modèle en cache (cloner pour avoir une nouvelle instance)
      const cachedModel = this.assetCache.get(fullPath)!;
      const modelClone = cachedModel.clone();
      
      // Appliquer les transformations
      modelClone.scale.copy(scale);
      modelClone.position.copy(position);
      modelClone.rotation.copy(rotation);
      
      // Ajouter le clone à la scène
      this.scene.add(modelClone);
    } else {
      // Charger le modèle FBX s'il n'est pas en cache
      try {
        this.fbxLoader.load(
          fileName,
          (fbx) => {
            console.log(`Modèle FBX chargé avec succès: ${fileName}`);
            // Appliquer la texture à tous les matériaux du modèle
            fbx.traverse((child) => {
              if (child instanceof THREE.Mesh) {
                child.castShadow = true;
                child.receiveShadow = true;
                
                // Appliquer la texture chargée avec des paramètres améliorés
                if (child.material) {
                  if (Array.isArray(child.material)) {
                    child.material.forEach((mat) => {
                      mat.map = texture;
                      this.enhanceMaterial(mat);
                    });
                  } else {
                    child.material.map = texture;
                    this.enhanceMaterial(child.material);
                  }
                }
              }
            });
            
            // Ajouter le modèle original au cache (avant application des transformations)
            this.assetCache.set(fullPath, fbx);
            
            // Créer un clone pour cette instance spécifique
            const modelInstance = fbx.clone();
            
            // Appliquer les transformations à l'instance
            modelInstance.scale.copy(scale);
            modelInstance.position.copy(position);
            modelInstance.rotation.copy(rotation);
            
            // Ajouter l'instance à la scène
            this.scene.add(modelInstance);
          },
          // Callback de progression
          (xhr) => {
            console.log(`${fileName} ${(xhr.loaded / xhr.total) * 100}% loaded`);
          },
          // Callback d'erreur
          (error) => {
            console.error(`Erreur lors du chargement du modèle ${fileName}:`, error);
            
            // En cas d'erreur, créer un objet de remplacement simple
            this.createFallbackObject(position, rotation, scale, texture);
          }
        );
      } catch (err) {
        console.error(`Exception lors du chargement du modèle ${fileName}:`, err);
        this.createFallbackObject(position, rotation, scale, texture);
      }
    }
  }

  // Améliorer l'apparence des matériaux
  private enhanceMaterial(material: THREE.Material): void {
    if (material instanceof THREE.MeshPhongMaterial || 
        material instanceof THREE.MeshStandardMaterial) {
      
      // Augmenter la luminosité des matériaux
      material.emissive = new THREE.Color(0x222222);
      material.emissiveIntensity = 0.1;
      
      // Augmenter la saturation des couleurs
      if (material.color) {
        const hsl = {h: 0, s: 0, l: 0};
        material.color.getHSL(hsl);
        material.color.setHSL(hsl.h, Math.min(hsl.s * 1.1, 1.0), hsl.l);
      }
      
      // Ajuster d'autres propriétés pour des visuels plus vifs
      if (material instanceof THREE.MeshPhongMaterial) {
        // Vérifier si le matériau est utilisé pour un arbre ou espace vert
        const materialName = material.name.toLowerCase();
        if (materialName.includes('tree') || materialName.includes('grass') || 
            materialName.includes('nature') || materialName.includes('plant')) {
          // Réduire fortement la brillance pour les éléments naturels
          material.shininess = 5; // Valeur faible pour un aspect mat
          material.flatShading = true;
        } else {
          // Pour les autres objets, brillance modérée
          material.shininess = 30; 
        }
      }
      
      // Pour MeshStandardMaterial
      if (material instanceof THREE.MeshStandardMaterial) {
        const materialName = material.name.toLowerCase();
        if (materialName.includes('tree') || materialName.includes('grass') || 
            materialName.includes('nature') || materialName.includes('plant')) {
          // Réduire fortement l'aspect métallique et augmenter la rugosité pour les éléments naturels
          material.metalness = 0.2;
          material.roughness = 0.7;
        } else {
          // Pour les autres objets
          material.metalness = 0.2;
          material.roughness = 0.7;
        }
      }
    }
  }

  // Méthode pour configurer un éclairage optimisé
  public setupEnhancedLighting(scene: THREE.Scene): void {
    // Lumière ambiante plus vive
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    scene.add(ambientLight);
    
    // Lumière directionnelle principale (comme le soleil)
    const mainLight = new THREE.DirectionalLight(0xffffff, 0.8);
    mainLight.position.set(50, 80, 50);
    mainLight.castShadow = true;
    
    // Améliorer la qualité des ombres
    mainLight.shadow.mapSize.width = 2048;
    mainLight.shadow.mapSize.height = 2048;
    mainLight.shadow.camera.near = 0.5;
    mainLight.shadow.camera.far = 500;
    mainLight.shadow.bias = -0.0005;
    
    // Définir la zone de projection des ombres
    const d = 100;
    mainLight.shadow.camera.left = -d;
    mainLight.shadow.camera.right = d;
    mainLight.shadow.camera.top = d;
    mainLight.shadow.camera.bottom = -d;
    
    scene.add(mainLight);
    
    // Lumière d'appoint pour adoucir les ombres
    const fillLight = new THREE.DirectionalLight(0xffffff, 0.3);
    fillLight.position.set(-50, 40, -50);
    scene.add(fillLight);
    
    // Lumière d'accentuation pour faire ressortir les détails
    const rimLight = new THREE.DirectionalLight(0xffffaa, 0.2);
    rimLight.position.set(0, 10, -80);
    scene.add(rimLight);
  }

  // Render a home marker at the specified position
  public renderHomeMarker(playerId: string, position: THREE.Vector3): void {
    // Check if we already have a marker for this player
    if (this.homeMarkers.has(playerId)) {
      // Update position if marker already exists
      const existingMarker = this.homeMarkers.get(playerId);
      if (existingMarker) {
        existingMarker.position.copy(position);
        return;
      }
    }
    
    // Create a group to hold the marker components
    const markerGroup = new THREE.Group();
    markerGroup.position.copy(position);
    
    // Create a cylindrical base for the marker
    const baseGeometry = new THREE.CylinderGeometry(0.5, 0.5, 0.1, 16);
    const baseMaterial = new THREE.MeshPhongMaterial({ 
      color: COLORS.HOME_MARKER,
      flatShading: true 
    });
    const base = new THREE.Mesh(baseGeometry, baseMaterial);
    base.position.y = 0.05; // Just above the ground
    markerGroup.add(base);
    
    // Create a pole for the marker
    const poleGeometry = new THREE.CylinderGeometry(0.1, 0.1, 1.5, 8);
    const poleMaterial = new THREE.MeshPhongMaterial({ 
      color: 0xffffff,
      flatShading: true 
    });
    const pole = new THREE.Mesh(poleGeometry, poleMaterial);
    pole.position.y = 0.85; // Half the height of the pole + base height
    markerGroup.add(pole);
    
    // Add the marker to the scene
    this.scene.add(markerGroup);
    
    // Store the marker in our map
    this.homeMarkers.set(playerId, markerGroup);
  }
  
  // Remove a home marker for a specific player
  public removeHomeMarker(playerId: string): void {
    const marker = this.homeMarkers.get(playerId);
    if (marker) {
      this.scene.remove(marker);
      this.homeMarkers.delete(playerId);
    }
  }
  
  // Clear all home markers
  public clearAllHomeMarkers(): void {
    this.homeMarkers.forEach((marker) => {
      this.scene.remove(marker);
    });
    this.homeMarkers.clear();
  }

  // Create and add a road sign at the specified position
  public addRoadSign(
    signId: string, 
    text: string, 
    position: THREE.Vector3,
    rotation: number = 0 // Rotation in radians, default facing forward
  ): void {
    if (!this.font) {
      console.warn('Font not loaded yet. Retrying in 100ms...');
      setTimeout(() => this.addRoadSign(signId, text, position, rotation), 100);
      return;
    }

    // Remove existing sign with the same ID if it exists
    this.removeRoadSign(signId);
    
    // Create a group to hold the sign components
    const signGroup = new THREE.Group();
    signGroup.position.copy(position);
    signGroup.position.y += 0.1; // Slightly above ground to avoid z-fighting
    signGroup.rotation.y = rotation; // Apply rotation around vertical axis
    
    // Create sign post
    const postGeometry = new THREE.CylinderGeometry(0.1, 0.1, 3, 8);
    const postMaterial = new THREE.MeshPhongMaterial({ 
      color: 0x000000, // Black post (changed from gray 0x808080)
      flatShading: true 
    });
    const post = new THREE.Mesh(postGeometry, postMaterial);
    post.position.y = 1.5; // Half the height
    signGroup.add(post);
    
    // Create temporary text to measure its size
    const tempTextGeometry = new TextGeometry(text, {
      font: this.font,
      size: 0.3,
      height: 0.05,
      curveSegments: 12,
      bevelEnabled: false
    });
    tempTextGeometry.computeBoundingBox();
    const textWidth = tempTextGeometry.boundingBox!.max.x - tempTextGeometry.boundingBox!.min.x;
    const textHeight = tempTextGeometry.boundingBox!.max.y - tempTextGeometry.boundingBox!.min.y;
    tempTextGeometry.dispose(); // Clean up temporary geometry

    // Calculate board dimensions with padding
    const padding = 0.3; // Padding on each side
    const boardWidth = textWidth + padding * 2;
    const boardHeight = Math.max(0.8, textHeight + padding * 2);
    
    // Create front side of sign board
    const boardGeometry = new THREE.BoxGeometry(boardWidth, boardHeight, 0.1);
    const boardMaterial = new THREE.MeshPhongMaterial({ 
      color: 0x000000, // Black
      flatShading: true 
    });
    const board = new THREE.Mesh(boardGeometry, boardMaterial);
    board.position.y = 2.5; // Position at top of post
    signGroup.add(board);
    
    // Create front side text
    const frontTextGeometry = new TextGeometry(text, {
      font: this.font,
      size: 0.3,
      height: 0.05,
      curveSegments: 12,
      bevelEnabled: false
    });
    const textMaterial = new THREE.MeshPhongMaterial({ 
      color: 0xFFFFFF, // White text
      flatShading: true 
    });
    const frontTextMesh = new THREE.Mesh(frontTextGeometry, textMaterial);
    
    // Center the text on the board
    frontTextGeometry.computeBoundingBox();
    frontTextMesh.position.set(
      -textWidth/2, // Center horizontally
      2.5 - boardHeight/2 + padding, // Align with bottom padding
      0.06 // Slightly in front of the board
    );
    signGroup.add(frontTextMesh);
    
    // Create back side of sign board (recto verso)
    const backBoard = board.clone();
    backBoard.rotation.y = Math.PI; // Rotate 180 degrees around Y axis
    signGroup.add(backBoard);
    
    // Create back side text (recto verso)
    const backTextGeometry = new TextGeometry(text, {
      font: this.font,
      size: 0.3,
      height: 0.05,
      curveSegments: 12,
      bevelEnabled: false
    });
    const backTextMesh = new THREE.Mesh(backTextGeometry, textMaterial);
    
    // Center the text on the back board, flipped 180 degrees
    backTextGeometry.computeBoundingBox();
    backTextMesh.position.set(
      textWidth/2, // Center horizontally, but reversed for back side
      2.5 - boardHeight/2 + padding, // Align with bottom padding
      -0.06 // Slightly behind the board
    );
    backTextMesh.rotation.y = Math.PI; // Rotate 180 degrees around Y axis
    signGroup.add(backTextMesh);
    
    // Add to scene and store reference
    this.scene.add(signGroup);
    this.roadSigns.set(signId, signGroup);
  }
  
  // Remove a road sign by ID
  public removeRoadSign(signId: string): void {
    const sign = this.roadSigns.get(signId);
    if (sign) {
      this.scene.remove(sign);
      this.roadSigns.delete(signId);
    }
  }
  
  // Clear all road signs
  public clearAllRoadSigns(): void {
    this.roadSigns.forEach((sign) => {
      this.scene.remove(sign);
    });
    this.roadSigns.clear();
  }

  // Rendu d'un poste de police
  private renderPoliceStation(i: number, j: number): void {
    const position = this.calculatePosition(i, j);
    const width = this.blockSize;
    const depth = this.blockSize;
    
    // Charger le modèle 3D du poste de police avec la texture commune
    this.loadFBXAsset(
      'cartooncity_141_fbx/Buildings/Police.fbx',
      new THREE.Vector3(
        position.x + width / 2,
        0, // Positionner au niveau du sol
        position.z + depth / 2
      ),
      new THREE.Euler(0, Math.PI / 2, 0), // Rotation de 90 degrés sur l'axe Y
      new THREE.Vector3(0.6, 0.6, 0.6) // Échelle pour s'adapter à la taille de la grille
    );
  
    // // Add the ironic sign next to the police station, facing perpendicular to the road
    // const signPosition = new THREE.Vector3(
    //   position.x + width + this.roadWidth/2, // Just to the right of the police station
    //   0,
    //   position.z + depth/2 // Aligned with the middle of the station
    // );
    // this.addRoadSign('police-sign', "Y'A PAS DE PANNEAU", signPosition, Math.PI / 2); 
    // // Rotate -90 degrees to face perpendicular to the road
  }     

  // Rendu d'un portail
  private renderPortal(i: number, j: number): void {
    const position = this.calculatePosition(i, j);
    const width = this.blockSize;
    const depth = this.blockSize;
    
    // Charger le modèle 3D du portail avec la texture commune
    this.loadFBXAsset(
      'cartooncity_141_fbx/Props/Luke_01.fbx',
      new THREE.Vector3(
        position.x + width / 2,
        0, // Positionner au niveau du sol
        position.z + depth / 2
      ),
      new THREE.Euler(0, Math.PI / 2, 0), // Rotation de 90 degrés sur l'axe Y
      new THREE.Vector3(1, 1, 1) // Échelle pour s'adapter à la taille de la grille
    );

    // Create portal group to contain all portal elements
    const exitPortalGroup = new THREE.Group();
    exitPortalGroup.position.set(
      position.x + width / 2,
      2, // Légèrement au-dessus du sol
      position.z + depth / 2
    );
    exitPortalGroup.rotation.x = 1.57; // Make portal horizontal instead of crooked
    exitPortalGroup.rotation.y = 0;
    exitPortalGroup.scale.set(0.1, 0.1, 0.1);

    // Create portal effect
    const exitPortalGeometry = new THREE.TorusGeometry(15, 2, 16, 100);
    const exitPortalMaterial = new THREE.MeshPhongMaterial({
        color: 0x00ff00,
        emissive: 0x00ff00,
        transparent: true,
        opacity: 0.8
    });
    const exitPortal = new THREE.Mesh(exitPortalGeometry, exitPortalMaterial);
    // Rotate torus to be flat/horizontal
    exitPortal.rotation.x = Math.PI / 2;
    exitPortalGroup.add(exitPortal);

    // Create portal inner surface
    const exitPortalInnerGeometry = new THREE.CircleGeometry(13, 32);
    const exitPortalInnerMaterial = new THREE.MeshBasicMaterial({
        color: 0x00ff00,
        transparent: true,
        opacity: 0.5,
        side: THREE.DoubleSide
    });
    const exitPortalInner = new THREE.Mesh(exitPortalInnerGeometry, exitPortalInnerMaterial);
    // Ensure the inner surface is perpendicular to the ground
    exitPortalInner.rotation.x = Math.PI / 2; // Rotate 90 degrees around X axis to face upward
    exitPortalGroup.add(exitPortalInner);
    
    // Add portal label
    const loader = new THREE.TextureLoader();
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (context) {
        canvas.width = 1024; // Double the width for better resolution
        canvas.height = 128; // Increase height for better visibility
        context.fillStyle = '#00b500'; // Green text
        context.font = 'bold 48px Nunito'; // Much larger font
        context.textAlign = 'center';
        context.textBaseline = 'middle';
        
        // Draw text with outline for better visibility
        const text = 'VIBEVERSE PORTAL 👉';
        context.strokeText(text, canvas.width/2, canvas.height/2);
        context.fillText(text, canvas.width/2, canvas.height/2);
    }
    
    const texture = new THREE.CanvasTexture(canvas);
    texture.needsUpdate = true; // Ensure texture updates
    
    // Create a larger label
    const labelGeometry = new THREE.PlaneGeometry(8, 1.5); // Much larger plane
    const labelMaterial = new THREE.MeshBasicMaterial({
        map: texture,
        transparent: true,
        side: THREE.DoubleSide,
        depthTest: true, // Ensures it's always visible
        depthWrite: true // Doesn't write to depth buffer
    });
    
    // Create the label and position it above the portal
    const label = new THREE.Mesh(labelGeometry, labelMaterial);
    label.position.copy(exitPortalGroup.position);
    label.position.y += 3; // Position well above the portal
    
    // Make the label always face the camera
    label.userData.isPortalLabel = true;
    
    // Add label to scene directly for better visibility
    this.scene.add(label);
    
    // Store the label reference on the portal group for cleanup purposes
    exitPortalGroup.userData.portalLabel = label;

    // Add a larger collision area for the portal while keeping it invisible
    const collisionBox = new THREE.Mesh(
      new THREE.BoxGeometry(5, 5, 5),
      new THREE.MeshBasicMaterial({
        color: 0x00ff00,
        transparent: true,
        opacity: 0.0 // Invisible but used for collision
      })
    );
    collisionBox.position.copy(exitPortalGroup.position);
    this.scene.add(collisionBox);

    // Add full portal group to scene
    this.scene.add(exitPortalGroup);

    // Create portal collision box - use the larger collision box
    const exitPortalBox = new THREE.Box3().setFromObject(collisionBox);

    // Store exit portal info for external access
    this.exitPortalBox = exitPortalBox;
    this.exitPortalGroup = exitPortalGroup;
    
    console.log("Portal rendered at position:", position);
  }
  
  // Rendu d'un pont horizontal (est-ouest)
  private renderHorizontalBridge(i: number, j: number): void {
    const position = this.calculatePosition(i, j);
    const width = this.blockSize * 3;  // Longueur du pont
    const depth = this.blockSize;      // Largeur du pont
    
    // Tablier du pont
    const bridgeDeckGeometry = new THREE.BoxGeometry(width, 0.3, depth);
    const bridgeDeckMaterial = new THREE.MeshPhongMaterial({ 
      color: 0x888888,  // Gris pour le pont
      shininess: 30
    });
    
    const bridgeDeck = new THREE.Mesh(bridgeDeckGeometry, bridgeDeckMaterial);
    bridgeDeck.position.set(
      position.x + width / 2,
      0.15,  // Altitude légèrement au-dessus de l'eau
      position.z + depth / 2
    );
    
    this.scene.add(bridgeDeck);
    
    // Garde-corps
    this.addRailings(position.x, position.z, width, depth, true);
    
    // Piliers du pont
    this.addBridgePillars(position.x, position.z, width, depth, true);
  }
  
  // Rendu d'un pont vertical (nord-sud)
  private renderVerticalBridge(i: number, j: number): void {
    const position = this.calculatePosition(i, j);
    const width = this.blockSize;      // Largeur du pont
    const depth = this.blockSize * 3;  // Longueur du pont
    
    // Tablier du pont
    const bridgeDeckGeometry = new THREE.BoxGeometry(width, 0.3, depth);
    const bridgeDeckMaterial = new THREE.MeshPhongMaterial({ 
      color: 0x888888,  // Gris pour le pont
      shininess: 30
    });
    
    const bridgeDeck = new THREE.Mesh(bridgeDeckGeometry, bridgeDeckMaterial);
    bridgeDeck.position.set(
      position.x + width / 2,
      0.15,  // Altitude légèrement au-dessus de l'eau
      position.z + depth / 2
    );
    
    this.scene.add(bridgeDeck);
    
    // Garde-corps
    this.addRailings(position.x, position.z, width, depth, false);
    
    // Piliers du pont
    this.addBridgePillars(position.x, position.z, width, depth, false);
  }
  
  // Fonction d'aide pour ajouter des garde-corps à un pont
  private addRailings(x: number, z: number, width: number, depth: number, isHorizontal: boolean): void {
    const railingHeight = 0.3;
    const railingWidth = isHorizontal ? width : 0.1;
    const railingDepth = isHorizontal ? 0.1 : depth;
    
    // Matériau pour les garde-corps
    const railingMaterial = new THREE.MeshPhongMaterial({ color: 0x444444 });
    
    // Garde-corps nord
    const northRailingGeometry = new THREE.BoxGeometry(railingWidth, railingHeight, railingDepth);
    const northRailing = new THREE.Mesh(northRailingGeometry, railingMaterial);
    
    // Garde-corps sud
    const southRailingGeometry = new THREE.BoxGeometry(railingWidth, railingHeight, railingDepth);
    const southRailing = new THREE.Mesh(southRailingGeometry, railingMaterial);
    
    if (isHorizontal) {
      // Pour un pont horizontal, les garde-corps sont au nord et au sud
      northRailing.position.set(x + width / 2, 0.45, z + 0.05);
      southRailing.position.set(x + width / 2, 0.45, z + depth - 0.05);
    } else {
      // Pour un pont vertical, les garde-corps sont à l'est et à l'ouest
      northRailing.position.set(x + 0.05, 0.45, z + depth / 2);
      southRailing.position.set(x + width - 0.05, 0.45, z + depth / 2);
    }
    
    this.scene.add(northRailing);
    this.scene.add(southRailing);
  }
  
  // Fonction d'aide pour ajouter des piliers à un pont
  private addBridgePillars(x: number, z: number, width: number, depth: number, isHorizontal: boolean): void {
    const pillarWidth = 0.5;
    const pillarHeight = 0.4;
    const pillarDepth = 0.5;
    const pillarMaterial = new THREE.MeshPhongMaterial({ color: 0x666666 });
    
    if (isHorizontal) {
      // Pour un pont horizontal, placer des piliers le long de l'axe x
      const pillarCount = Math.floor(width / 5);
      for (let i = 0; i < pillarCount; i++) {
        const pillarX = x + (i + 0.5) * (width / pillarCount);
        
        // Pilier nord
        const northPillarGeometry = new THREE.BoxGeometry(pillarWidth, pillarHeight, pillarDepth);
        const northPillar = new THREE.Mesh(northPillarGeometry, pillarMaterial);
        northPillar.position.set(pillarX, -0.05, z + 0.25);
        
        // Pilier sud
        const southPillarGeometry = new THREE.BoxGeometry(pillarWidth, pillarHeight, pillarDepth);
        const southPillar = new THREE.Mesh(southPillarGeometry, pillarMaterial);
        southPillar.position.set(pillarX, -0.05, z + depth - 0.25);
        
        this.scene.add(northPillar);
        this.scene.add(southPillar);
      }
    } else {
      // Pour un pont vertical, placer des piliers le long de l'axe z
      const pillarCount = Math.floor(depth / 5);
      for (let i = 0; i < pillarCount; i++) {
        const pillarZ = z + (i + 0.5) * (depth / pillarCount);
        
        // Pilier est
        const eastPillarGeometry = new THREE.BoxGeometry(pillarWidth, pillarHeight, pillarDepth);
        const eastPillar = new THREE.Mesh(eastPillarGeometry, pillarMaterial);
        eastPillar.position.set(x + 0.25, -0.05, pillarZ);
        
        // Pilier ouest
        const westPillarGeometry = new THREE.BoxGeometry(pillarWidth, pillarHeight, pillarDepth);
        const westPillar = new THREE.Mesh(westPillarGeometry, pillarMaterial);
        westPillar.position.set(x + width - 0.25, -0.05, pillarZ);
        
        this.scene.add(eastPillar);
        this.scene.add(westPillar);
      }
    }
  }
  
  // Rendu d'une station-service
  private renderGazStation(i: number, j: number): void {
    const position = this.calculatePosition(i, j);
    const width = this.blockSize;
    const depth = this.blockSize;
    
    // Charger le modèle 3D de la station-service avec la texture commune
    this.loadFBXAsset(
      'cartooncity_141_fbx/Buildings/Gas_Station_01.fbx',
      new THREE.Vector3(
        position.x + width / 2,
        0, // Positionner au niveau du sol
        position.z + depth / 2
      ),
      new THREE.Euler(0, Math.PI / 2, 0), // Rotation de 90 degrés sur l'axe Y
      new THREE.Vector3(0.6, 0.6, 0.6) // Échelle pour s'adapter à la taille de la grille
    );
    
    // Ajouter une deuxième instance décalée de 5 unités sur l'axe x
    this.loadFBXAsset(
      'cartooncity_141_fbx/Buildings/Gas_Station_01.fbx',
      new THREE.Vector3(
        position.x + width / 2 + 5, // Décalage de 5 unités sur l'axe x
        0, // Positionner au niveau du sol
        position.z + depth / 2
      ),
      new THREE.Euler(0, Math.PI / 2, 0), // Rotation de 90 degrés sur l'axe Y
      new THREE.Vector3(0.6, 0.6, 0.6) // Échelle pour s'adapter à la taille de la grille
    );
  }
  
  // Fonction d'aide pour ajouter les supports de l'auvent
  private addCanopySupports(x: number, z: number, width: number, depth: number): void {
    const supportGeometry = new THREE.CylinderGeometry(0.15, 0.15, 2, 8);
    const supportMaterial = new THREE.MeshPhongMaterial({ color: 0xcccccc });
    
    // Quatre supports aux coins de l'auvent
    const support1 = new THREE.Mesh(supportGeometry, supportMaterial);
    support1.position.set(x - width / 2, 1.1, z - depth / 2);
    
    const support2 = new THREE.Mesh(supportGeometry, supportMaterial);
    support2.position.set(x + width / 2, 1.1, z - depth / 2);
    
    const support3 = new THREE.Mesh(supportGeometry, supportMaterial);
    support3.position.set(x - width / 2, 1.1, z + depth / 2);
    
    const support4 = new THREE.Mesh(supportGeometry, supportMaterial);
    support4.position.set(x + width / 2, 1.1, z + depth / 2);
    
    this.scene.add(support1);
    this.scene.add(support2);
    this.scene.add(support3);
    this.scene.add(support4);
  }
  
  // Fonction d'aide pour ajouter les pompes à essence
  private addGasPumps(x: number, z: number, width: number, depth: number): void {
    const pumpGeometry = new THREE.BoxGeometry(0.4, 1, 0.4);
    const pumpMaterial = new THREE.MeshPhongMaterial({ color: 0xff0000 });
    
    // Deux rangées de pompes à essence
    for (let i = 0; i < 2; i++) {
      for (let j = 0; j < 2; j++) {
        const pump = new THREE.Mesh(pumpGeometry, pumpMaterial);
        pump.position.set(
          x - width / 4 + i * (width / 2),
          0.5,  // Moitié de la hauteur
          z - depth / 4 + j * (depth / 2)
        );
        this.scene.add(pump);
      }
    }
  }
  
  // Rendu du marché noir
  private renderBlackMarket(i: number, j: number): void {
    let position = this.calculatePosition(i+1, j+1);
    const width = this.blockSize * 2 + this.roadWidth * 3;
    const depth = this.blockSize * 2 + this.roadWidth * 3;

    
    // Ajouter le sol du marché noir avec l'asset BG.fbx
    this.loadFBXAsset(
      'cartooncity_141_fbx/Road/BG.fbx',
      new THREE.Vector3(
        position.x,
        -0.19, // Légèrement sous le niveau du sol
        position.z
      ),
      new THREE.Euler(0, 0, 0),
      new THREE.Vector3(width*0.038, 1, depth*0.038) // Échelle adaptée à la taille du bloc
    );
    
    // Charger le modèle 3D du supermarché avec la texture commune
    this.loadFBXAsset(
      'cartooncity_141_fbx/Buildings/Supermarket.fbx',
      new THREE.Vector3(
        position.x - this.blockSize / 2.5,
        0, // Positionner au niveau du sol
        position.z + this.roadWidth / 2
      ),
      new THREE.Euler(0, Math.PI / 2, 0), // Rotation de 90 degrés sur l'axe Y
      new THREE.Vector3(0.8, 0.9, 0.8) // Échelle pour s'adapter à la taille de la grille
    );

    // Add a road sign near the entrance, facing towards the road
    const signPosition = new THREE.Vector3(
      position.x -this.roadWidth / 2, // At the center of the market
      0,
      position.z + this.roadWidth/2 // Just in front of the market
    );
    this.addRoadSign('black-market-sign', 'Black Market', signPosition, Math.PI); // Rotate to face the road
  }
  
  // Fonction d'aide pour ajouter des stands au marché noir
  private addMarketStalls(x: number, z: number, width: number, depth: number): void {
    const stallCount = 8;  // Nombre de stands autour du marché
    const radius = Math.min(width, depth) * 0.4;  // Rayon du cercle des stands
    
    for (let i = 0; i < stallCount; i++) {
      // Calculer la position en cercle
      const angle = (i / stallCount) * Math.PI * 2;
      const stallX = x + width / 2 + Math.cos(angle) * radius;
      const stallZ = z + depth / 2 + Math.sin(angle) * radius;
      
      // Stand (petit abri)
      const stallWidth = 0.8;
      const stallDepth = 0.8;
      
      // Base du stand
      const tableGeometry = new THREE.BoxGeometry(stallWidth, 0.1, stallDepth);
      const tableMaterial = new THREE.MeshPhongMaterial({ color: 0x4d2c14 }); // Brun foncé
      const table = new THREE.Mesh(tableGeometry, tableMaterial);
      
      table.position.set(stallX, 0.5, stallZ);
      
      // Auvent/parasol du stand
      const awningGeometry = new THREE.ConeGeometry(stallWidth * 0.8, 0.4, 8);
      const awningMaterial = new THREE.MeshPhongMaterial({ 
        color: 0x8B0000, // Rouge foncé
        flatShading: true
      });
      const awning = new THREE.Mesh(awningGeometry, awningMaterial);
      
      awning.position.set(stallX, 1.2, stallZ);
      
      this.scene.add(table);
      this.scene.add(awning);
      
      // Objets sur le stand (marchandises)
      this.addStallItems(stallX, stallZ, stallWidth, stallDepth);
    }
  }
  
  // Fonction d'aide pour ajouter des objets sur les stands
  private addStallItems(x: number, z: number, width: number, depth: number): void {
    const itemCount = 2 + Math.floor(Math.random() * 3); // 2-4 objets par stand
    
    for (let i = 0; i < itemCount; i++) {
      // Position aléatoire sur le stand
      const itemX = x - width * 0.3 + Math.random() * width * 0.6;
      const itemZ = z - depth * 0.3 + Math.random() * depth * 0.6;
      
      // Choisir entre cube et sphère pour les objets
      if (Math.random() > 0.5) {
        // Petit cube (boîte)
        const boxSize = 0.15 + Math.random() * 0.1;
        const boxGeometry = new THREE.BoxGeometry(boxSize, boxSize, boxSize);
        const boxMaterial = new THREE.MeshPhongMaterial({ 
          color: Math.random() * 0xffffff,
          flatShading: true
        });
        const box = new THREE.Mesh(boxGeometry, boxMaterial);
        
        box.position.set(itemX, 0.6 + boxSize / 2, itemZ);
        this.scene.add(box);
      } else {
        // Petite sphère (objet rond)
        const sphereRadius = 0.08 + Math.random() * 0.08;
        const sphereGeometry = new THREE.SphereGeometry(sphereRadius, 8, 8);
        const sphereMaterial = new THREE.MeshPhongMaterial({ 
          color: Math.random() * 0xffffff,
          flatShading: true
        });
        const sphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
        
        sphere.position.set(itemX, 0.6 + sphereRadius, itemZ);
        this.scene.add(sphere);
      }
    }
  }
  
  // Fonction d'aide pour ajouter un effet de fumée/brouillard
  private addSmoke(x: number, z: number): void {
    // Créer plusieurs particules de fumée semi-transparentes
    const smokeCount = 12;
    
    for (let i = 0; i < smokeCount; i++) {
      // Position aléatoire autour du centre
      const offsetX = (Math.random() - 0.5) * 2;
      const offsetZ = (Math.random() - 0.5) * 2;
      
      // Taille aléatoire
      const smokeSize = 0.5 + Math.random() * 0.5;
      
      // Utiliser une sphère plate comme particule de fumée
      const smokeGeometry = new THREE.SphereGeometry(smokeSize, 8, 4);
      const smokeMaterial = new THREE.MeshPhongMaterial({ 
        color: 0x222222,
        transparent: true,
        opacity: 0.2 + Math.random() * 0.1,
        flatShading: true
      });
      
      const smoke = new THREE.Mesh(smokeGeometry, smokeMaterial);
      
      // Position aléatoire
      smoke.position.set(
        x + offsetX,
        0.5 + Math.random() * 0.5,  // Hauteur variable
        z + offsetZ
      );
      
      // Aplatir la sphère pour qu'elle ressemble à un nuage
      smoke.scale.y = 0.3;
      
      this.scene.add(smoke);
    }
  }

  public getExitPortalBox(): THREE.Box3 | null {
    return this.exitPortalBox;
  }

  public getExitPortalGroup(): THREE.Group | null {
    return this.exitPortalGroup;
  }

  /**
   * Update portal labels to face the camera
   * @param camera The camera to face
   */
  public updatePortalLabels(camera: THREE.Camera): void {
    // Find all portal labels in the scene
    this.scene.traverse((object) => {
      if (object instanceof THREE.Mesh && object.userData.isPortalLabel) {
        // Make the label face the camera
        object.lookAt(camera.position);
      }
    });
  }

  // Public method to load FBX assets that can be used by other classes
  public loadModelAsset(
    assetPath: string, 
    position: THREE.Vector3, 
    rotation: THREE.Euler = new THREE.Euler(0, 0, 0),
    scale: THREE.Vector3 = new THREE.Vector3(1, 1, 1),
    parent: THREE.Object3D | null = null,
    customColor: number | null = null
  ): void {
    // Extract the path parts to set up the loader correctly
    const pathParts = assetPath.split('/');
    const basePath = pathParts.slice(0, pathParts.length - 1).join('/') + '/';
    this.fbxLoader.setPath(basePath);
    
    // The file name without the path
    const fileName = pathParts[pathParts.length - 1];
    
    // If the common texture isn't loaded yet, retry later
    if (this.commonTexture === null) {
      setTimeout(() => {
        this.loadModelAsset(assetPath, position, rotation, scale, parent, customColor);
      }, 100);
      return;
    }
    
    // Utiliser simplement le processus standard qui fonctionne déjà pour tous les autres objets
    if (parent) {
      // Si un parent est fourni, on utilise le même algorithme que processFBXLoading
      // mais on ajoute au parent au lieu de la scène
      const fullPath = basePath + fileName;
      // Vérifier si le modèle est déjà dans le cache
      if (this.assetCache.has(fullPath)) {
        // Utiliser le modèle en cache
        const cachedModel = this.assetCache.get(fullPath)!;
        const modelClone = cachedModel.clone();
        
        // On sait que this.commonTexture n'est pas null à ce stade
        const texture = this.commonTexture!;
        
        // Appliquer les transformations et la couleur
        this.applyTransformsAndMaterials(
          modelClone, 
          position, 
          rotation, 
          scale, 
          customColor, 
          texture
        );
        
        // Ajouter au parent
        parent.add(modelClone);
      } else {
        // Charger le modèle FBX
        this.fbxLoader.load(
          fileName,
          (fbx) => {
            // Ajouter au cache d'abord
            this.assetCache.set(fullPath, fbx.clone());
            
            // On sait que this.commonTexture n'est pas null à ce stade
            const texture = this.commonTexture!;
            
            // Appliquer les transformations et la couleur/texture
            this.applyTransformsAndMaterials(
              fbx, 
              position, 
              rotation, 
              scale, 
              customColor, 
              texture
            );
            
            // Ajouter au parent
            parent.add(fbx);
          },
          // Callback de progression
          (xhr) => {
            console.log(`${fileName} ${(xhr.loaded / xhr.total) * 100}% loaded`);
          },
          // Callback d'erreur
          (error) => {
            console.error(`Error loading model ${fileName}:`, error);
          }
        );
      }
    } else {
      // Si pas de parent, utiliser la méthode standard qui ajoute à la scène
      this.loadFBXAsset(assetPath, position, rotation, scale);
    }
  }
  
  // Méthode d'aide pour appliquer les transformations et matériaux/textures
  private applyTransformsAndMaterials(
    object: THREE.Object3D,
    position: THREE.Vector3,
    rotation: THREE.Euler,
    scale: THREE.Vector3,
    customColor: number | null,
    texture: THREE.Texture
  ): void {
    // Appliquer les transformations
    object.position.copy(position);
    object.rotation.copy(rotation);
    object.scale.copy(scale);
    
    // Parcourir tous les enfants pour appliquer matériaux ou textures
    object.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        // Activer les ombres
        child.castShadow = true;
        child.receiveShadow = true;
        
        // Appliquer la couleur personnalisée ou la texture
        if (customColor !== null) {
          // Appliquer la couleur
          if (Array.isArray(child.material)) {
            child.material = child.material.map(() => {
              return new THREE.MeshPhongMaterial({
                color: customColor,
                flatShading: true,
                shininess: 30
              });
            });
          } else {
            child.material = new THREE.MeshPhongMaterial({
              color: customColor,
              flatShading: true,
              shininess: 30
            });
          }
        } else {
          // Appliquer la texture
          if (Array.isArray(child.material)) {
            child.material.forEach((mat) => {
              mat.map = texture;
              this.enhanceMaterial(mat);
            });
          } else {
            child.material.map = texture;
            this.enhanceMaterial(child.material);
          }
        }
      }
    });
  }
} 